<script context="module" lang="ts">
	import { APPLE_PAY_MERCHANT_ID, payWithApplePay } from "#lib/applepay.js";
	import { browser } from "$app/env";

	const applePayAvailable = USE_PERMALINK ? false : isApplePayAvailable();
	async function isApplePayAvailable(): Promise<boolean> {
		if (browser && window.ApplePaySession) {
			const promise = window.ApplePaySession.canMakePaymentsWithActiveCard(APPLE_PAY_MERCHANT_ID);
			return new Promise(resolve => {
				promise.then(function (canMakePayments) {
					resolve(canMakePayments);
				});
				promise.catch(function () {
					resolve(false);
				});
			});
		}
		return false;
	}
</script>

<script lang="ts">
	import { FieldMaskSchema } from "@bufbuild/protobuf/wkt";
	import { ButtonGroup, Heading, Img, Input, Label, Select } from "flowbite-svelte";
	import { getContext, onMount } from "svelte";
	import type { Suite } from "#gen/bundles/v1/bundle_pb.js";
	import { CommerceDataSchema } from "#gen/bundles/v1/commerce_pb.js";
	import { PostalAddressSchema } from "#gen/google/type/postal_address_pb.js";
	import { LineItemSchema, type Checkout, type PaymentInfo, type Variant } from "#gen/products/v1/product_pb.js";
	import type { ProductClient } from "#lib/api/products.js";
	import Icon from "#lib/components/Icon.svelte";
	import P from "#lib/components/P.svelte";
	import type { PushFn } from "#lib/components/StackController.svelte";
	import ContactInfo from "#lib/components/flow/editor/content/commerce/ContactInfo.svelte";
	import { USE_PERMALINK } from "#lib/conversion.js";
	import { formatPrice } from "#lib/money.js";
	import { create, protoInt64 } from "#lib/protobuf-compat.js";
	import Button from "#lib/ui/Button/index.js";
	import type { Writable } from "svelte/store";

	const pageState: Writable<{ suite: Writable<Suite>; pageId: string }> = getContext("pageState");
	const productClient: ProductClient = getContext("productClient");
	const suite: Writable<Suite> = $pageState.suite;
	const data = $suite.data?.type.case === "commerce" ? $suite.data?.type.value : create(CommerceDataSchema, {});

	const push = getContext<PushFn>("push");

	const options = buildOptions(data.product?.variants);

	export let id: string;
	export let checkout: Writable<Checkout> = getContext("checkout");
	export let paymentInfo: Writable<PaymentInfo>;

	let selectedVariant = data.product?.variants.find(({ id }) => id === $checkout?.lineItems[0].variantId) ?? data.product?.variants[0];
	let selectedQuantity = 1;

	const values = { ...data.product?.variants[0].selectedOptions };
	let saving = false;

	onMount(async () => {
		if ($checkout || USE_PERMALINK) {
			return;
		}
		saving = true;
		try {
			await beginCheckout();
		} finally {
			saving = false;
		}
	});

	function buildOptions(variants: Variant[] | undefined): Record<string, string[]> {
		if (!variants) {
			return {};
		}
		const options: Record<string, string[]> = {};
		for (const variant of variants) {
			if (!variant.selectedOptions) {
				continue;
			}
			Object.keys(variant.selectedOptions).forEach((name: string) => {
				if (!(name in options)) {
					options[name] = [] as string[];
				}
				const value = variant.selectedOptions[name];
				if (options[name].findIndex(current => current === value) == -1) {
					options[name].push(value);
				}
			});
		}
		return options;
	}

	async function beginCheckout() {
		if (!selectedVariant) {
			return;
		}

		const address = create(PostalAddressSchema, {
			regionCode: data.integration!.paymentSettings?.countryCode ?? "US",
			locality: "New York",
			administrativeArea: "NY",
		});
		$checkout = await productClient.beginCheckout(
			data.integration!.id,
			address,
			[
				create(LineItemSchema, {
					variantId: selectedVariant.id,
					quantity: protoInt64.parse(selectedQuantity),
				}),
			],
			selectedVariant.requiresShipping,
		);
	}

	async function save() {
		if (!selectedVariant) {
			return;
		}

		saving = true;
		try {
			$checkout.lineItems[0].variantId = selectedVariant.id;
			$checkout.lineItems[0].quantity = protoInt64.parse(selectedQuantity);
			$checkout = await productClient.updateCheckout($checkout, create(FieldMaskSchema, { paths: ["line_items"] }));
		} finally {
			saving = false;
		}
		push(ContactInfo!, { props: { id, checkout, paymentInfo } });
	}

	async function selectVariant(option: string, event: Event) {
		values[option] = (event.target as HTMLInputElement)?.value;
		const newVariant = findSelectedVariant();
		if (selectedVariant?.id === newVariant.id) {
			return;
		}
		selectedVariant = newVariant;
	}

	function findSelectedVariant(): Variant {
		const variant = data.product?.variants.find(variant => {
			if (!variant.selectedOptions) {
				return false;
			}
			for (const option of Object.keys(variant.selectedOptions)) {
				if (variant.selectedOptions[option] !== values[option]) {
					return false;
				}
			}
			return true;
		});
		return variant!;
	}

	async function isApplePayEnabled(): Promise<boolean> {
		if (!data.integration?.paymentSettings?.supportedDigitalWallets.find(value => value === "APPLE_PAY")) {
			return false;
		}
		return applePayAvailable;
	}

	async function applePay() {
		if (selectedVariant && $checkout.lineItems[0].variantId !== selectedVariant.id) {
			saving = true;
			try {
				$checkout.lineItems[0].variantId = selectedVariant?.id;
				$checkout = await productClient.updateCheckout($checkout, create(FieldMaskSchema, { paths: ["line_items"] }));
			} finally {
				saving = false;
			}
		}
		await payWithApplePay(productClient, data.integration!, $checkout);
	}

	async function redirectPermalink() {
		if (!selectedVariant) {
			return;
		}

		const variantId = selectedVariant.id.substring(selectedVariant.id.lastIndexOf("/") + 1);
		const attributes = {
			sid: `${$suite.id}:${$pageState.pageId}`,
		};
		const win = window.open("", "_blank")!;
		const url = await productClient.createCartPermalink(data.integration!, variantId, selectedQuantity, attributes);
		win.location.href = url;
	}

	function addItem() {
		if (!selectedVariant) {
			return;
		}
		if (!selectedVariant?.availableForSale) {
			return;
		}
		if (selectedVariant?.currentlyNotInStock) {
			selectedQuantity++;
			return;
		}
		if (!data.product?.totalInventory || data.product?.totalInventory < 0) {
			selectedQuantity++;
			return;
		}
		if (selectedQuantity + 1 <= data.product?.totalInventory) {
			selectedQuantity++;
		}
	}

	function removeItem() {
		if (selectedQuantity - 1 < 1) {
			return;
		}
		selectedQuantity--;
	}
</script>

<div class="flex grow flex-col">
	<div class="flex items-center gap-4 pb-2">
		<div class="flex-none">
			<Img class="h-36 w-32 rounded-lg object-cover" src={selectedVariant?.image?.url} />
		</div>
		<div class="flex grow flex-col gap-1">
			<Heading tag="h6" class="heading-consumer">{data.product?.title}</Heading>
			<P class="p-consumer">
				{#if selectedVariant?.compareAtPrice?.units}
					<span class="pr-1 text-gray-300 line-through">{formatPrice(selectedVariant?.compareAtPrice)}</span>
				{/if}
				{formatPrice(selectedVariant?.price)}
			</P>
			<ButtonGroup class="shadow-none">
				<Button data-action-name="decrement-product-count" aria-label="Decrease quantity" class="stepper-consumer left" color="light" on:click={removeItem}>
					<Icon class="w-5 text-gray-900" name="minus-small" />
				</Button>
				<Input defaultClass="stepper-consumer" type="text" bind:value={selectedQuantity} disabled />
				<Button data-action-name="increase-product-count" aria-label="Increase quantity" class="stepper-consumer right" color="light" on:click={addItem}>
					<Icon class="w-5 text-gray-900" name="plus-small" />
				</Button>
			</ButtonGroup>
		</div>
	</div>
	<form class="grow py-2">
		{#if (data.product?.variants?.length ?? 0) > 1}
			{#each Object.keys(options) as option (option)}
				<div class="mb-4">
					<Label for={option} defaultClass="label-consumer">{option}</Label>
					<Select
						id={option}
						defaultClass="select-consumer"
						name={option}
						items={options[option].map(option => {
							return { name: option, value: option };
						})}
						value={options[option][0]}
						on:change={event => selectVariant(option, event)}
					/>
				</div>
			{/each}
		{/if}
	</form>
	<div class="sticky bottom-0 bg-white py-4">
		{#if USE_PERMALINK}
			<div class="w-full">
				<Button data-action-name="checkout-with-permalink" class="btn-consumer-primary" color="dark" size="lg" on:click={redirectPermalink} disabled={saving}>Checkout</Button>
			</div>
		{:else}
			{#await isApplePayEnabled() then applePayEnabled}
				<div class="grid grid-cols-{applePayEnabled ? 2 : 1} gap-2">
					{#if applePayEnabled}
						<!-- svelte-ignore a11y-no-static-element-interactions -->
						<!-- svelte-ignore a11y-click-events-have-key-events -->
						<apple-pay-button buttonstyle="black" type="plain" on:click={applePay} disabled={saving} locale="en-US"></apple-pay-button>
					{/if}
					<Button data-action-name="checkout-with-apply-pay" class="btn-consumer-primary" color={applePayEnabled ? "light" : "dark"} size="lg" on:click={save} disabled={!selectedVariant?.availableForSale || saving}>Checkout</Button>
				</div>
			{/await}
		{/if}
	</div>
</div>
